// =====================================================
// PATIENTS API CLIENT - Multi-Centro + Nº Historia
// =====================================================
import { api } from './client';
import type {
  Patient,
  PaginatedPatients,
  PatientSearchResult,
  PatientCompanySearchResult,
  PatientLinkResult,
  LinkedSiteInfo,
  CreatePatientRequest,
  UpdatePatientRequest,
  PatientQueryParams,
  PatientSearchParams,
  LinkPatientData,
  PatientSummary,
} from '../types/patient';

const BASE_URL = '/patients';

export const patientsApi = {
  /**
   * Crear nuevo paciente
   */
  create: async (data: CreatePatientRequest): Promise<Patient> => {
    return api.post<Patient>(BASE_URL, data);
  },

  /**
   * Obtener paciente por ID
   */
  getById: async (id: string): Promise<Patient> => {
    return api.get<Patient>(`${BASE_URL}/${id}`);
  },

  /**
   * Listar pacientes con paginación
   */
  getAll: async (params?: PatientQueryParams): Promise<PaginatedPatients> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    const url = query ? `${BASE_URL}?${query}` : BASE_URL;
    return api.get<PaginatedPatients>(url);
  },

  /**
   * Buscar pacientes por documento, email o teléfono (solo en el centro actual)
   */
  search: async (params: PatientSearchParams): Promise<PatientSearchResult[]> => {
    const searchParams = new URLSearchParams();
    searchParams.append('query', params.query);
    if (params.searchBy) {
      searchParams.append('searchBy', params.searchBy);
    }
    return api.get<PatientSearchResult[]>(`${BASE_URL}/search?${searchParams.toString()}`);
  },

  /**
   * Buscar pacientes en TODA la empresa (para vincular a este centro)
   */
  searchCompany: async (params: PatientSearchParams): Promise<PatientCompanySearchResult[]> => {
    const searchParams = new URLSearchParams();
    searchParams.append('query', params.query);
    if (params.searchBy) {
      searchParams.append('searchBy', params.searchBy);
    }
    return api.get<PatientCompanySearchResult[]>(`${BASE_URL}/search-company?${searchParams.toString()}`);
  },

  /**
   * Vincular paciente existente al centro actual
   */
  linkToSite: async (data: LinkPatientData): Promise<PatientLinkResult> => {
    return api.post<PatientLinkResult>(`${BASE_URL}/link`, data);
  },

  /**
   * Obtener centros donde está vinculado un paciente
   */
  getPatientSites: async (patientId: string): Promise<LinkedSiteInfo[]> => {
    return api.get<LinkedSiteInfo[]>(`${BASE_URL}/${patientId}/sites`);
  },

  /**
   * Cambiar estado del paciente en el centro actual o actualizar nº historia
   */
  updateSiteStatus: async (
    patientId: string, 
    data: { isActive?: boolean; medicalRecordNumber?: string }
  ): Promise<LinkedSiteInfo> => {
    return api.patch<LinkedSiteInfo>(`${BASE_URL}/${patientId}/site-status`, data);
  },

  /**
   * Actualizar paciente
   */
  update: async (id: string, data: UpdatePatientRequest): Promise<Patient> => {
    return api.patch<Patient>(`${BASE_URL}/${id}`, data);
  },

  /**
   * Desactivar paciente en el centro actual (soft delete)
   */
  delete: async (id: string): Promise<{ success: boolean }> => {
    return api.delete<{ success: boolean }>(`${BASE_URL}/${id}`);
  },

  /**
   * Reactivar paciente en el centro actual
   */
  reactivate: async (id: string): Promise<Patient> => {
    return api.patch<Patient>(`${BASE_URL}/${id}/reactivate`);
  },

  /**
   * Cambia el estado de vínculo del paciente en CUALQUIER centro.
   * Permisos aplicados en backend según rol del usuario.
   */
  updateAnySiteStatus: async (
    patientId: string,
    targetSiteId: string,
    isActive: boolean,
  ): Promise<LinkedSiteInfo> => {
    return api.patch<LinkedSiteInfo>(`${BASE_URL}/${patientId}/sites/${targetSiteId}/status`, { isActive });
  },

  /**
   * KPIs agregados del paciente para la cabecera 360°.
   */
  getSummary: async (patientId: string): Promise<PatientSummary> => {
    return api.get<PatientSummary>(`${BASE_URL}/${patientId}/summary`);
  },
};
